import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { isWhitespace } from 'src/util/string';
import {
  latitudeMinutesRangeValidator,
  latitudeRangeValidator,
  longitudeMinutesRangeValidator,
  longitudeRangeValidator,
} from '../coordinate-validators';
import {
  CoordinateV1,
  ensureValidCoordinates,
  ParsedCoordinateValues,
  parseInput,
} from '../coordinate.model';
import { CoordinateKind } from '../coordinate.model.v2';

interface CoordinateInputValue {
  display: string | null;
  value: number | null;
}
interface PreviousInputCoordinates {
  latitude: CoordinateInputValue;
  longitude: CoordinateInputValue;
}
interface SelectedControls {
  displayCtrl: UntypedFormControl;
  valueCtrl: AbstractControl;
  previous: CoordinateInputValue;
  maxLength: number;
  kind: CoordinateKind;
}

function maxLengthIfNumber(maxLength: number): ValidatorFn {
  return (control: AbstractControl) => {
    // eslint-disable-next-line use-isnan
    if (control.value != null && !Number.isNaN(Number(control.value))) {
      const maxLengthResult = Validators.maxLength(maxLength)(control);
      if (maxLengthResult != null) {
        return {
          maxLengthIfNumber: maxLengthResult.maxLength,
        };
      }
    }
    return null;
  };
}

@Component({
  selector: 'tcs-coordinates',
  templateUrl: './coordinates.component.html',
  styleUrls: ['./coordinates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoordinatesComponent implements OnInit, OnDestroy {
  private _fxFlex = false;
  private _required = false;
  @Input()
  public formGroup: UntypedFormGroup;
  @Input()
  public prefixLabel = false;
  @Input()
  public fxLayoutGap = '5px';
  @Input()
  set fxFlex(value: boolean) {
    this._fxFlex = true;
  }
  get fxFlex(): boolean {
    return this._fxFlex;
  }
  @Input()
  set required(value: string | boolean) {
    this._required = typeof value === 'string' ? true : value;
  }
  get required() {
    return this._required;
  }

  @Input()
  public outlineStyle = false;

  @Input()
  public cssClass: any = '';

  public latDisplayCtrl: UntypedFormControl;
  public lngDisplayCtrl: UntypedFormControl;

  public latMaxLength = 7;
  public lngMaxLength = 8;
  public _latValidators = Validators.compose([
    latitudeRangeValidator,
    latitudeMinutesRangeValidator,
  ]) as ValidatorFn;
  public _lngValidators: ValidatorFn = Validators.compose([
    longitudeRangeValidator,
    longitudeMinutesRangeValidator,
  ]) as ValidatorFn;

  private _onDestroy$ = new Subject();
  private _previous: PreviousInputCoordinates = {
    latitude: { display: null, value: null },
    longitude: { display: null, value: null },
  };

  get latValueCtrl(): UntypedFormControl {
    return this.formGroup.get('latitude') as UntypedFormControl;
  }

  get lngValueCtrl(): UntypedFormControl {
    return this.formGroup.get('longitude') as UntypedFormControl;
  }

  constructor(private _log: Logger) {}

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  public ngOnInit() {
    this._latValidators = Validators.compose([
      this.latValueCtrl.validator,
      this._latValidators,
    ]) as ValidatorFn;

    this._lngValidators = Validators.compose([
      this.lngValueCtrl.validator,
      this._lngValidators,
    ]) as ValidatorFn;

    this.latDisplayCtrl = new UntypedFormControl(null, this._latValidators);
    this.lngDisplayCtrl = new UntypedFormControl(null, this._lngValidators);
    this.setupControl(CoordinateKind.Latitude);
    this.setupControl(CoordinateKind.Longitude);
  }

  public onfocus($event: any, kind: CoordinateKind) {
    const { displayCtrl, maxLength } = this.selectControls(kind);
    if (kind === CoordinateKind.Latitude) {
      this.latValueCtrl.markAsDirty();
    } else {
      this.lngValueCtrl.markAsDirty();
    }

    if (typeof displayCtrl.value === 'string') {
      // set max length validator in addition to others
      if (kind === CoordinateKind.Longitude) {
        displayCtrl.setValidators([maxLengthIfNumber(maxLength), this._lngValidators]);
      } else {
        displayCtrl.setValidators([maxLengthIfNumber(maxLength), this._latValidators]);
      }
      const value = displayCtrl.value.replace(/[^0-9]/g, '');
      displayCtrl.setValue(value);
      $event.target.select();
    }
  }

  public onblur($event: any, kind: CoordinateKind) {
    const { displayCtrl, valueCtrl } = this.selectControls(kind);

    if (kind === CoordinateKind.Longitude) {
      displayCtrl.setValidators(Validators.compose([this._lngValidators, valueCtrl.validator]));
      this.lngValueCtrl.markAsTouched();
    } else {
      displayCtrl.setValidators(Validators.compose([this._latValidators, valueCtrl.validator]));
      this.latValueCtrl.markAsTouched();
    }

    this.updateValue(displayCtrl.value, kind);
  }

  public onkeydown(e: KeyboardEvent) {
    if (
      (e.key >= '0' && e.key <= '9') || // 0-9
      ((e.ctrlKey || e.metaKey) &&
        (e.key === 'c' || e.key === 'a' || e.key === 'x' || e.key === 'v')) || // ctrl+[c|a|x|v]
      // eslint-disable-next-line import/no-deprecated
      (e.keyCode >= 35 && e.keyCode <= 39) || // home, end, left, right
      // eslint-disable-next-line import/no-deprecated
      e.keyCode === 8 || // backspace
      // eslint-disable-next-line import/no-deprecated
      e.keyCode === 9 || // tab
      // eslint-disable-next-line import/no-deprecated
      e.keyCode === 46 || // delete
      e.key === '.' // decimal degrees input
    ) {
      return;
    } else {
      e.preventDefault();
    }
  }

  private setupControl(kind: CoordinateKind) {
    const { displayCtrl, valueCtrl, previous } = this.selectControls(kind);

    if (valueCtrl.disabled) {
      displayCtrl.disable();
    }

    if (valueCtrl.value != null) {
      this.updateValue(valueCtrl.value, kind);
    } else {
      this.updateValue(null, kind);
    }

    valueCtrl.statusChanges.pipe(takeUntil(this._onDestroy$)).subscribe((status) => {
      valueCtrl.disabled ? displayCtrl.disable() : displayCtrl.enable();
    });

    valueCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this._onDestroy$))
      .subscribe((value) => {
        if (value !== previous.value) {
          this.updateValue(value, kind);
        }
      });
  }

  private updateValue(value: string | number | null, kind: CoordinateKind) {
    const { valueCtrl, displayCtrl, previous } = this.selectControls(kind);

    if (value == null || isWhitespace(value)) {
      previous.display = null;
      previous.value = null;
      displayCtrl.setValue(null);
      valueCtrl.setValue(null);
    } else {
      let parsedCoord: ParsedCoordinateValues | undefined;
      try {
        parsedCoord = parseInput(value, kind);
      } catch (err) {
        // If there was an error parsing, do nothing
        // Log because we're curious about the input, but this should not be considered an error/warning scenario
        this._log.info(`Error parsing coordinate input ${kind} value "${value}": ${err}`);
        return;
      }
      const errors = ensureValidCoordinates(
        parsedCoord.value,
        parsedCoord.minutes + parsedCoord.seconds / 60,
        parsedCoord.direction,
        kind,
      );
      if (errors == null || errors.length === 0) {
        const coord = new CoordinateV1(parsedCoord.value, kind);
        const displayValue = coord.toString();
        displayCtrl.setValue(displayValue);
        if (displayValue !== previous.display) {
          previous.display = displayValue;
          previous.value = coord.value;
          valueCtrl.setValue(coord.value);
        }
      }
    }
  }

  private selectControls(kind: CoordinateKind): SelectedControls {
    if (kind === CoordinateKind.Latitude) {
      return {
        valueCtrl: this.latValueCtrl,
        displayCtrl: this.latDisplayCtrl,
        previous: this._previous.latitude,
        maxLength: this.latMaxLength,
        kind,
      };
    } else {
      return {
        valueCtrl: this.lngValueCtrl,
        displayCtrl: this.lngDisplayCtrl,
        previous: this._previous.longitude,
        maxLength: this.lngMaxLength,
        kind,
      };
    }
  }
}
